import React from 'react';
import { Link } from 'react-router-dom';
import './Industries.css';

const industries = [
  {
    id: 'automotive', icon: '🚗', title: 'Automotive', tagline: 'Built for the Road Ahead',
    image: 'https://images.pexels.com/photos/3729464/pexels-photo-3729464.jpeg?auto=compress&cs=tinysrgb&w=800',
    desc: 'Robust electronics for vehicle systems requiring durability, vibration resistance, and performance under extreme temperature conditions. We manufacture to IATF 16949-aligned processes.',
    products: ['ADAS Sensor Boards', 'EV Battery Management Systems', 'Infotainment Modules', 'Body Control Units', 'Vehicle Telematics Devices'],
    stat: '500K+', statLabel: 'Automotive boards delivered',
  },
  {
    id: 'medical', icon: '🏥', title: 'Medical Devices', tagline: 'Precision That Saves Lives',
    image: 'https://images.pexels.com/photos/4386466/pexels-photo-4386466.jpeg?auto=compress&cs=tinysrgb&w=800',
    desc: 'Precision manufacturing meeting stringent regulatory compliance and quality standards. Class 100K clean room facility, IPC-A-610 Class 3 assembly, and ISO 13485-aligned quality processes.',
    products: ['Patient Monitoring Systems', 'Diagnostic Device Electronics', 'Implantable Device PCBs', 'Medical IoT Sensors', 'Portable Health Devices'],
    stat: 'Class 100K', statLabel: 'Clean room facility',
  },
  {
    id: 'industrial', icon: '⚙️', title: 'Industrial & IoT', tagline: 'Enabling Smart Factories',
    image: 'https://global.hitachi-solutions.com/wp-content/uploads/2020/07/industrial-iot-benefits.png',
    desc: 'Connected sensors, PLCs, HMIs, automation equipment and industrial control systems. We deliver electronics that perform reliably in harsh industrial environments 24×7.',
    products: ['Industrial IoT Gateways', 'Motor Drive Controllers', 'PLC I/O Modules', 'Industrial HMI Panels', 'Condition Monitoring Systems'],
    stat: '98.7%', statLabel: 'First-pass yield',
  },
  {
    id: 'energy', icon: '⚡', title: 'Energy Systems', tagline: 'Powering a Cleaner World',
    image: 'https://images.pexels.com/photos/9875414/pexels-photo-9875414.jpeg?auto=compress&cs=tinysrgb&w=800',
    desc: 'Power management electronics, renewable energy controllers, EV charging infrastructure, and energy storage management systems — designed for efficiency and long operational life.',
    products: ['MPPT Solar Controllers', 'EV Charging Station Boards', 'Battery Management Systems', 'Smart Grid Monitors', 'Power Conversion Electronics'],
    stat: '50MW', statLabel: 'Solar parks supported',
  },
  {
    id: 'consumer', icon: '📱', title: 'Consumer Electronics', tagline: 'Delightful Products at Scale',
    image: 'https://images.pexels.com/photos/1714208/pexels-photo-1714208.jpeg?auto=compress&cs=tinysrgb&w=800',
    desc: 'Smart devices, wearables, connected home products, and consumer IoT — where user experience, form factor, and reliability are equally critical. We handle high-volume runs efficiently.',
    products: ['Smart Home Hubs', 'Wearable Sensors', 'Audio Electronics', 'Smart Kitchen Appliances', 'Connected Lighting Controllers'],
    stat: '1M+', statLabel: 'Consumer units/year',
  },
  {
    id: 'telecom', icon: '📡', title: 'Telecom & Networking', tagline: 'Connecting the Unconnected',
    image: 'https://images.pexels.com/photos/4481326/pexels-photo-4481326.jpeg?auto=compress&cs=tinysrgb&w=800',
    desc: 'High-frequency RF modules, antenna boards, network access equipment, and edge computing hardware — manufactured with the precision that 5G and beyond demands.',
    products: ['RF Front-End Modules', 'Network Access Points', 'Edge Computing Boards', 'Antenna Array Assemblies', 'Protocol Converter Devices'],
    stat: '5G', statLabel: 'Ready manufacturing',
  },
];

export default function Industries() {
  return (
    <main className="industries-page page-wrapper">
      <section className="page-hero">
        <div className="page-hero__bg" />
        <div className="container page-hero__inner">
          <div className="section-tag" style={{ background: 'rgba(0,194,224,0.15)', color: 'var(--cyan)' }}>Industries</div>
          <h1 className="page-hero__title">Serving Every <span>Sector</span></h1>
          <p className="page-hero__subtitle">
            From automotive to consumer electronics — our manufacturing platform adapts to the unique compliance, quality, and volume demands of every industry.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="industries-detail-list">
            {industries.map((ind, i) => (
              <div key={ind.id} id={ind.id} className={`industry-block${i % 2 !== 0 ? ' industry-block--reverse' : ''}`}>
                <div className="industry-block__visual">
                  <div className="industry-visual-inner industry-visual-inner--img">
                    <img src={ind.image} alt={ind.title} className="industry-block__img" loading="lazy" />
                    <div className="industry-img-overlay" />
                    <div className="industry-stat-pill">
                      <strong>{ind.stat}</strong>
                      <span>{ind.statLabel}</span>
                    </div>
                    <div className="industry-img-icon">{ind.icon}</div>
                  </div>
                </div>
                <div className="industry-block__content">
                  <div className="section-tag">{ind.title}</div>
                  <h2 className="svc-title">{ind.tagline}</h2>
                  <p className="svc-desc">{ind.desc}</p>
                  <div className="industry-products">
                    <strong>Products We Build:</strong>
                    <div className="product-tags">
                      {ind.products.map(p => (
                        <span key={p} className="product-tag">{p}</span>
                      ))}
                    </div>
                  </div>
                  <Link to="/contact" className="btn-primary" style={{ marginTop: '24px' }}>Discuss Your Requirements →</Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section industries-certs">
        <div className="container">
          <div className="section-center">
            <div className="section-tag">Quality Standards</div>
            <h2 className="section-title">Built to the <span>Highest Standards</span></h2>
            <p className="section-subtitle" style={{ margin: '0 auto 48px' }}>
              Our quality management systems align with global industry certifications.
            </p>
          </div>
          <div className="certs-grid">
            {['ISO 9001:2015', 'IPC-A-610 Class 2 & 3', 'RoHS Compliant', 'REACH Compliant', 'ISO 14001', 'IATF 16949 Aligned'].map(c => (
              <div key={c} className="cert-card">
                <span className="cert-check">✓</span>
                <strong>{c}</strong>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="cta-banner">
        <div className="container cta-banner__inner">
          <div className="cta-banner__text">
            <h2>Your Industry. <span>Our Expertise.</span></h2>
            <p>Tell us about your product and compliance requirements — we'll design a manufacturing solution around your needs.</p>
          </div>
          <div className="cta-banner__actions">
            <Link to="/contact" className="btn-primary">Get a Free Consultation →</Link>
            <Link to="/services" className="btn-outline-white">Our Services</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
